// external imports
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';

// internal imports
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import { StatusPill, ApprovalProgress, formatDate, parseTaskError } from './common';

// stat card used for Active / Completed / Pending Reviews
const StatCard = ({ label, value, accent }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 200,
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      p: 2.5,
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', color: '#94a3b8' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 32, fontWeight: 700, color: accent || '#0f172a', mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

const STATUS_FILTERS = ['all', 'active', 'completed', 'cancelled'];

const TasksList = () => {
  const apiCaller = useApiCaller();
  const navigate = useNavigate();

  const [tasks, setTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [unauthorized, setUnauthorized] = React.useState(false);

  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');

  const fetchTasks = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setUnauthorized(false);
      const response = await apiCaller.get('/task/created');
      setTasks(response?.data?.data?.tasks ?? []);
    } catch (err) {
      const parsed = parseTaskError(err, 'Failed to load tasks. Please try again.');
      setError(parsed.message);
      setUnauthorized(parsed.unauthorized);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // stat totals
  const stats = React.useMemo(() => {
    let active = 0;
    let completed = 0;
    let pendingReviews = 0; // translations submitted, awaiting the manager's review
    tasks.forEach((t) => {
      if (t.status === 'active') active += 1;
      if (t.status === 'completed') completed += 1;
      pendingReviews += t.progress?.submitted ?? 0;
    });
    return { active, completed, pendingReviews };
  }, [tasks]);

  // filtered view
  const visibleTasks = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (query && !(t.taskName || '').toLowerCase().includes(query)) return false;
      return true;
    });
  }, [tasks, search, statusFilter]);

  const gridTemplate = '2.2fr 1fr 0.9fr 1.6fr 1fr 1fr 0.6fr';

  const headerCellSx = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: '#94a3b8',
    textTransform: 'uppercase',
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* title + create */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
              Translation Tasks
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Create translation tasks and track per-language review progress.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/tasks/create')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              px: 2.5,
              py: 1.1,
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' },
            }}
          >
            Create Task
          </Button>
        </Box>

        {/* stat cards */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
          <StatCard label="ACTIVE" value={stats.active} accent="#0f172a" />
          <StatCard label="COMPLETED" value={stats.completed} accent="#0f172a" />
          <StatCard label="PENDING REVIEWS" value={stats.pendingReviews} accent="#0f172a" />
        </Box>

        {/* search + filter */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search tasks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flexGrow: 1,
              minWidth: 220,
              maxWidth: 360,
              background: '#fff',
              borderRadius: '10px',
            }}
            InputProps={{
              sx: { borderRadius: '10px' },
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150, background: '#fff', borderRadius: '10px' }}
            InputProps={{ sx: { borderRadius: '10px' } }}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s} value={s}>
                {s === 'all' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* table card */}
        <Box
          sx={{
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography
                sx={{ fontWeight: 700, color: unauthorized ? '#b45309' : '#b91c1c', mb: 0.5 }}
              >
                {unauthorized ? 'Not authorized' : 'Something went wrong'}
              </Typography>
              <Typography sx={{ color: unauthorized ? '#92400e' : '#dc2626', fontSize: 14 }}>
                {error}
              </Typography>
            </Box>
          ) : tasks.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                No tasks yet
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: 14, mb: 2 }}>
                Create your first translation task to get started.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/tasks/create')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                }}
              >
                Create Task
              </Button>
            </Box>
          ) : (
            <>
              {/* header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  alignItems: 'center',
                  gap: 1,
                  px: 3,
                  py: 2,
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <Typography sx={headerCellSx}>Task Name</Typography>
                <Typography sx={headerCellSx}>Type</Typography>
                <Typography sx={headerCellSx}>Languages</Typography>
                <Typography sx={headerCellSx}>Progress</Typography>
                <Typography sx={headerCellSx}>Status</Typography>
                <Typography sx={headerCellSx}>Created</Typography>
                <Typography sx={{ ...headerCellSx, textAlign: 'right' }} />
              </Box>

              {/* rows */}
              {visibleTasks.map((task, index) => {
                const total = task.progress?.total ?? 0;
                const approved = task.progress?.approved ?? 0;
                return (
                  <Box
                    key={task._id}
                    onClick={() => navigate(`/tasks/${task._id}`)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: gridTemplate,
                      alignItems: 'center',
                      gap: 1,
                      px: 3,
                      py: 2,
                      cursor: 'pointer',
                      borderBottom:
                        index === visibleTasks.length - 1 ? 'none' : '1px solid #f1f5f9',
                      '&:hover': { background: '#fafbff' },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14.5, pr: 1 }}>
                      {task.taskName}
                    </Typography>
                    <Typography
                      sx={{ color: '#64748b', fontSize: 14, textTransform: 'capitalize' }}
                    >
                      {task.taskType || '—'}
                    </Typography>
                    <Typography sx={{ color: '#334155', fontSize: 14 }}>{total}</Typography>
                    <ApprovalProgress approved={approved} total={total} />
                    <Box>
                      <StatusPill status={task.status} />
                    </Box>
                    <Typography sx={{ color: '#64748b', fontSize: 13.5 }}>
                      {formatDate(task.createdAt)}
                    </Typography>
                    <Typography
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tasks/${task._id}`);
                      }}
                      sx={{
                        color: '#4f46e5',
                        fontSize: 14,
                        fontWeight: 600,
                        textAlign: 'right',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      View
                    </Typography>
                  </Box>
                );
              })}

              {visibleTasks.length === 0 && (
                <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, py: 5 }}>
                  No tasks match your search.
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default TasksList;
